/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  updateProfile,
  User as FirebaseUser
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  getDocFromServer
} from "firebase/firestore";
import { getStorage } from "firebase/storage";

import firebaseConfig from "../firebase-applet-config.json";

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const storage = getStorage(app);

// Authentication Providers
export const googleProvider = new GoogleAuthProvider();

// Connection Verification (from Firebase skill guidelines)
async function verifyFirebaseConnection() {
  try {
    // Attempt standard connection check
    await getDocFromServer(doc(db, 'test_connection', 'ping'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Firebase is offline. Please check your configuration and network connectivity.");
    }
  }
}
verifyFirebaseConnection();

// Define operation codes & Firestore error handling wrapper
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

export const isFirestoreQuotaError = (err: any): boolean => {
  if (!err) return false;
  const msg = (err.message || String(err)).toLowerCase();
  const code = (err.code || "").toLowerCase();
  return (
      code === "resource-exhausted" ||
      msg.includes("resource-exhausted") ||
      msg.includes("quota exceeded") ||
      msg.includes("quota-exceeded") ||
      msg.includes("exhausted")
  );
};

let quotaExceededCallback: ((isExceeded: boolean) => void) | null = null;

export function registerQuotaExceededListener(callback: (isExceeded: boolean) => void) {
  quotaExceededCallback = callback;
}

export function notifyQuotaExceeded() {
  if (quotaExceededCallback) {
    quotaExceededCallback(true);
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  if (isFirestoreQuotaError(error)) {
    console.warn("Firestore resource limit reached. Initiating grace scenario to preserve local operations.");
    notifyQuotaExceeded();
    return; // Recover gracefully by not throwing to prevent blank screens
  }
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export {
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  updateProfile,
  doc,
  getDoc,
  setDoc,
  onSnapshot
};
export type { FirebaseUser };
